import { Grid, Group, Input, Pagination, ScrollArea } from '@mantine/core';
import { useDebouncedValue, useElementSize, useMediaQuery } from '@mantine/hooks';
import axios, { CancelTokenSource } from 'axios';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { getDistanceFromTop, PageData, ChunkItem, getChunksPage } from '@mygpt/app';

import { SvgSearch } from '@/components/Common/icon';
import TableLoading from '@/components/TableLoading';

import TableItem from './item';

export const FileChunksList = () => {
  const { t } = useTranslation();

  const { datasetId, fileId } = useParams();

  const matches = useMediaQuery('(min-width: 640px)');
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [initLoading, setInitLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const { height: tableHeight, ref: tableRef } = useElementSize();
  const isInitRef = useRef(true);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [chunks, setChunks] = useState<ChunkItem[]>([]);

  const boxRef = useRef<HTMLElement>(null);
  const boxHeight = useMemo(() => {
    if (!boxRef.current) return '0px';
    return `${getDistanceFromTop(boxRef.current) + 16}px`;
  }, [boxRef.current]);

  const [filesListPageInfo, setFilesListPageInfo] = useState<Omit<PageData, 'items'>>({
    page: 1,
    pages: 1,
    size: 12,
    total: 0,
  });

  const selectItem = useCallback((itemId: string) => {
    setSelectedIds((ids) => {
      if (ids.includes(itemId)) {
        return ids.filter((id) => id !== itemId);
      }
      return [...ids, itemId];
    });
  }, []);

  const getChunks = useCallback(async () => {
    if (!fileId) throw new Error('fileId is required');
    setListLoading(true);

    cancelTokenSourceRef.current = axios.CancelToken.source();
    await getChunksPage(
      {
        datasetId,
        fileId,
        page: debouncedSearch ? 1 : filesListPageInfo.page,
        size: debouncedSearch ? 24 : filesListPageInfo.size,
        query: debouncedSearch,
      },
      cancelTokenSourceRef.current.token
    )
      .then((res) => {
        const { items, pages, size, total } = res.data;
        setChunks(items);
        setFilesListPageInfo((pageInfo) => {
          return {
            ...pageInfo,
            pages,
            size,
            total,
          };
        });
      })
      .finally(() => {
        setListLoading(false);
        setInitLoading(false);
        if (isInitRef.current) isInitRef.current = false;
      });
  }, [datasetId, debouncedSearch, fileId, filesListPageInfo.page, filesListPageInfo.size]);

  useLayoutEffect(() => {
    getChunks();
  }, [getChunks]);

  const searchParams = new URLSearchParams(window.location.search);

  return (
    <section className="flex flex-col relative" ref={boxRef} style={{ height: `calc(100vh - ${boxHeight})` }}>
      <section className="flex-none">
        <Group justify="space-between" mb={20}>
          <section>{searchParams.get('title')}</section>
          <section>
            <Input
              className="grow lg:grow-0 lg:min-w-[30%]"
              leftSection={<SvgSearch />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={t('apis.search_placeholder')}
            />
          </section>
        </Group>
      </section>

      <section className={`${!initLoading && 'flex-auto'} relative flex flex-col`} ref={tableRef}>
        {initLoading || listLoading ? (
          <TableLoading />
        ) : (
          <>
            {!chunks.length && (
              <section className="h-[400px] flex items-center justify-center min-h-[200px] bg-white rounded-[20px] overflow-hidden border border-[#F8F6FF]">
                {t('no_data')}
              </section>
            )}
            {!!chunks.length && (
              <>
                <section className="flex-auto">
                  <section className="h-full">
                    <ScrollArea.Autosize
                      mah={listLoading ? 120 : tableHeight - (filesListPageInfo.pages > 1 ? 52 : 0)}
                      h={listLoading ? 120 : tableHeight - (filesListPageInfo.pages > 1 ? 52 : 0)}
                      offsetScrollbars
                    >
                      <Grid className="m-0" gutter={0}>
                        {chunks.map((item) => {
                          return (
                            <Grid.Col span={6} key={item.id}>
                              <TableItem
                                key={item.id}
                                item={item}
                                selected={selectedIds.includes(item.id)}
                                onSelectedChange={() => selectItem(item.id)}
                                showScore={!!debouncedSearch}
                              />
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                    </ScrollArea.Autosize>
                  </section>
                </section>
                <section className="flex-none">
                  {filesListPageInfo.pages > 1 && !debouncedSearch && (
                    <section className="px-2 sm:px-8 mt-5">
                      <Pagination
                        value={filesListPageInfo.page}
                        total={filesListPageInfo.pages}
                        size={matches ? 'md' : 'sm'}
                        onChange={(page) => {
                          setFilesListPageInfo((pageInfo) => {
                            return {
                              ...pageInfo,
                              page,
                            };
                          });
                        }}
                      />
                    </section>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </section>
    </section>
  );
};
